/**
 * PortalSystem - Handles teleportation portals
 * 
 * Creates portal pairs and manages teleportation with cooldown.
 * 
 * @module systems/PortalSystem
 */

import Phaser from 'phaser';
import BaseSystem from './BaseSystem.js';
import { TIMING } from '../configs/GameConfig.js';

export default class PortalSystem extends BaseSystem {
    constructor(scene) {
        super(scene);
        this.labelA = 'portalA';
        this.labelB = 'portalB';
    }

    /**
     * Create portals from level config
     * @param {Object} config - Level configuration
     */
    create(config) {
        if (!config.portals) return;

        config.portals.forEach(p => {
            const color = p.color || 0x7B68EE;
            
            // Portal A
            const portalA = this.scene.matter.add.circle(p.x1, p.y1, 30, {
                isStatic: true,
                isSensor: true,
                label: this.labelA
            });

            // Portal B
            const portalB = this.scene.matter.add.circle(p.x2, p.y2, 30, {
                isStatic: true,
                isSensor: true,
                label: this.labelB
            });

            // Visuals
            const visualA = this.createPortalVisual(p.x1, p.y1, color);
            const visualB = this.createPortalVisual(p.x2, p.y2, color);

            this.items.push({
                a: { body: portalA, x: p.x1, y: p.y1, visual: visualA },
                b: { body: portalB, x: p.x2, y: p.y2, visual: visualB },
                lastTeleportTime: 0
            });
        });

        this.isActive = this.items.length > 0;
    }

    /**
     * Create swirling portal visual
     * @param {number} x - X position
     * @param {number} y - Y position
     * @param {number} color - Portal color
     * @returns {Phaser.GameObjects.Container}
     */
    createPortalVisual(x, y, color) {
        const container = this.scene.add.container(x, y);
        
        // Outer ring
        const outerRing = this.scene.add.circle(0, 0, 35, color, 0.3);
        container.add(outerRing);
        
        // Inner ring
        const innerRing = this.scene.add.circle(0, 0, 25, color, 0.5);
        container.add(innerRing);

        // Center
        const center = this.scene.add.circle(0, 0, 15, 0xffffff, 0.8);
        container.add(center);

        // Swirl effect
        this.scene.tweens.add({
            targets: container,
            rotation: Math.PI * 2,
            duration: 3000,
            repeat: -1
        });

        this.scene.tweens.add({
            targets: [outerRing, innerRing],
            scale: 1.2,
            alpha: 0.2,
            duration: 1000,
            yoyo: true,
            repeat: -1
        });

        return container;
    }

    /**
     * Check if a body belongs to this system
     * @param {Object} body - Physics body
     * @returns {boolean}
     */
    ownsBody(body) {
        return body.label === this.labelA || body.label === this.labelB;
    }

    /**
     * Handle portal teleportation
     * @param {Object} body - Portal body
     * @param {Object} player - Player reference
     * @returns {boolean} True if handled
     */
    handleCollision(body, player) {
        if (!this.ownsBody(body)) return false;
        
        this.teleport(body.label, player);
        return true;
    }

    /**
     * Teleport player through portal
     * @param {string} portalLabel - Which portal was touched
     * @param {Object} player - Player reference
     */
    teleport(portalLabel, player) {
        if (!player || !player.sprite) return;

        const now = this.scene.time.now;
        
        for (const portal of this.items) {
            // Check cooldown
            if (now - portal.lastTeleportTime < TIMING.PORTAL_COOLDOWN) continue;
            
            const isPortalA = portalLabel === this.labelA;
            const source = isPortalA ? portal.a : portal.b;
            const dest = isPortalA ? portal.b : portal.a;

            // Check proximity
            const dx = Math.abs(player.sprite.x - source.x);
            const dy = Math.abs(player.sprite.y - source.y);
            
            if (dx < 50 && dy < 50) {
                // Teleport player
                this.scene.matter.body.setPosition(player.sprite.body, {
                    x: dest.x,
                    y: dest.y
                });

                // Visual effects
                this.scene.cameras.main.flash(200, 150, 100, 255);
                this.createTeleportParticles(source.x, source.y);
                this.createTeleportParticles(dest.x, dest.y);

                portal.lastTeleportTime = now;
                break;
            }
        }
    }

    /**
     * Create teleport particle effect
     * @param {number} x - X position
     * @param {number} y - Y position
     */
    createTeleportParticles(x, y) {
        for (let i = 0; i < 12; i++) {
            const angle = (i / 12) * Math.PI * 2;
            const particle = this.scene.add.circle(x, y, 8, 0x7B68EE);
            
            this.scene.tweens.add({
                targets: particle,
                x: x + Math.cos(angle) * 80,
                y: y + Math.sin(angle) * 80,
                alpha: 0,
                scale: 0.1,
                duration: 500,
                onComplete: () => particle.destroy()
            });
        }
    }

    /**
     * Cleanup
     */
    destroy() {
        this.items.forEach(portal => {
            if (portal.a.visual) portal.a.visual.destroy();
            if (portal.b.visual) portal.b.visual.destroy();
        });
        super.destroy();
    }
}
